import React, { useState, useEffect } from 'react';
import { Shuffle, User, Building, MapPin, MessageCircle, Calendar, Phone, Mail, Star } from 'lucide-react';

interface Contact {
  id: string;
  name: string;
  role: string;
  company: string;
  location: string;
  interest: string;
  followup: string;
  phone: string;
  email: string;
  avatar: string;
  priority: 'high' | 'medium' | 'low';
}

const AnimatedContactCard = () => {
  const contacts: Contact[] = [
    {
      id: '1',
      name: 'Sarah Chen',
      role: 'Product Manager',
      company: 'TechCorp',
      location: 'TechConf 2024, Booth #47',
      interest: 'AI partnerships & marketing tools',
      followup: 'Coffee meeting next week',
      phone: '+1 (555) 123-4567',
      email: 'sarah.chen@techcorp.com',
      avatar: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=2',
      priority: 'high'
    },
    {
      id: '2',
      name: 'Michael Rodriguez',
      role: 'Senior Developer',
      company: 'InnovateLab',
      location: 'DevMeetup SF, Main Hall',
      interest: 'React optimization & performance',
      followup: 'Share code examples',
      phone: '+1 (555) 987-6543',
      email: 'michael.r@innovatelab.io',
      avatar: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=2',
      priority: 'medium'
    },
    {
      id: '3',
      name: 'Emma Thompson',
      role: 'Marketing Director',
      company: 'GrowthCo',
      location: 'Marketing Summit, VIP Lounge',
      interest: 'B2B growth strategies',
      followup: 'Partnership discussion',
      phone: '+1 (555) 456-7890',
      email: 'emma.t@growthco.com',
      avatar: 'https://images.pexels.com/photos/1130626/pexels-photo-1130626.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=2',
      priority: 'high'
    },
    {
      id: '4',
      name: 'David Kim',
      role: 'Startup Founder',
      company: 'NextGen AI',
      location: 'Startup Pitch Night, Stage 2',
      interest: 'AI/ML collaboration',
      followup: 'Demo scheduling',
      phone: '+1 (555) 321-0987',
      email: 'david@nextgenai.com',
      avatar: 'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=2',
      priority: 'medium'
    },
    {
      id: '5',
      name: 'Lisa Wang',
      role: 'VP of Engineering',
      company: 'CloudTech',
      location: 'Tech Leadership Forum',
      interest: 'Cloud infrastructure & scaling',
      followup: 'Technical consultation',
      phone: '+1 (555) 654-3210',
      email: 'lisa.wang@cloudtech.com',
      avatar: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=2',
      priority: 'high'
    },
    {
      id: '6',
      name: 'Alex Johnson',
      role: 'UX Designer',
      company: 'DesignStudio',
      location: 'Design Conference, Workshop Room',
      interest: 'User experience & accessibility',
      followup: 'Portfolio review',
      phone: '+1 (555) 789-0123',
      email: 'alex.j@designstudio.com',
      avatar: 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=2',
      priority: 'medium'
    },
    {
      id: '7',
      name: 'Rachel Green',
      role: 'Sales Director',
      company: 'SalesForce Pro',
      location: 'Sales Summit, Main Stage',
      interest: 'B2B sales automation',
      followup: 'Demo presentation',
      phone: '+1 (555) 234-5678',
      email: 'rachel.g@salesforcepro.com',
      avatar: 'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=2',
      priority: 'high'
    },
    {
      id: '8',
      name: 'Tom Wilson',
      role: 'Data Scientist',
      company: 'DataCorp',
      location: 'AI Conference, Research Hall',
      interest: 'Machine learning & analytics',
      followup: 'Research collaboration',
      phone: '+1 (555) 345-6789',
      email: 'tom.w@datacorp.com',
      avatar: 'https://images.pexels.com/photos/1040880/pexels-photo-1040880.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=2',
      priority: 'medium'
    },
    {
      id: '9',
      name: 'Maria Garcia',
      role: 'Product Designer',
      company: 'CreativeLab',
      location: 'Product Design Meetup',
      interest: 'Design systems & prototyping',
      followup: 'Design system discussion',
      phone: '+1 (555) 456-7890',
      email: 'maria.g@creativelab.com',
      avatar: 'https://images.pexels.com/photos/1181424/pexels-photo-1181424.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=2',
      priority: 'high'
    },
    {
      id: '10',
      name: 'James Brown',
      role: 'CTO',
      company: 'TechVision',
      location: 'Leadership Summit, Executive Lounge',
      interest: 'Technology strategy & innovation',
      followup: 'Strategic partnership',
      phone: '+1 (555) 567-8901',
      email: 'james.b@techvision.com',
      avatar: 'https://images.pexels.com/photos/1043474/pexels-photo-1043474.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&dpr=2',
      priority: 'high'
    }
  ];

  const [currentContactIndex, setCurrentContactIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [animationPhase, setAnimationPhase] = useState<'visible' | 'disappearing' | 'appearing'>('visible');
  const [hasReachedEnd, setHasReachedEnd] = useState(false);

  const currentContact = contacts[currentContactIndex];

  const nextContact = () => {
    if (hasReachedEnd || animationPhase !== 'visible') return;
    
    setAnimationPhase('disappearing');
    setIsVisible(false);
    
    setTimeout(() => {
      const nextIndex = currentContactIndex + 1;
      if (nextIndex >= contacts.length) {
        setHasReachedEnd(true);
        setAnimationPhase('visible');
        setIsVisible(true);
        return;
      }
      
      setCurrentContactIndex(nextIndex);
      setAnimationPhase('appearing');
      
      setTimeout(() => {
        setIsVisible(true);
        setTimeout(() => {
          setAnimationPhase('visible');
        }, 300);
      }, 100);
    }, 500);
  };

  // Auto-shuffle every 3 seconds until we reach the end
  useEffect(() => {
    if (hasReachedEnd) return;
    
    const interval = setInterval(() => {
      nextContact();
    }, 3000);

    return () => clearInterval(interval);
  }, [currentContactIndex, hasReachedEnd, animationPhase]);

  const priorityColors = {
    high: 'bg-red-500',
    medium: 'bg-yellow-500',
    low: 'bg-green-500'
  };

  const priorityLabels = {
    high: 'High Priority',
    medium: 'Medium Priority',
    low: 'Low Priority'
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="relative overflow-hidden">
        {/* Main Contact Card */}
        <div className={`bg-white rounded-2xl relative transition-all duration-500 ease-out transform ${
          animationPhase === 'disappearing' 
            ? 'opacity-0 scale-95 translate-y-4' 
            : animationPhase === 'appearing'
            ? 'opacity-0 scale-105 -translate-y-4'
            : 'opacity-100 scale-100 translate-y-0'
        } ${
          isVisible && animationPhase === 'visible' ? 'opacity-100' : ''
        }`}>
          
          {/* Card Header */}
          <div className="relative p-6 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-2xl text-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Contact Profile</h3>
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 ${priorityColors[currentContact.priority]} rounded-full ${!hasReachedEnd ? 'animate-pulse' : ''}`}></div>
                {!hasReachedEnd && (
                  <button
                    onClick={nextContact}
                    disabled={animationPhase !== 'visible'}
                    className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors disabled:opacity-50"
                  >
                    <Shuffle className={`w-4 h-4 ${animationPhase !== 'visible' ? 'animate-spin' : ''}`} />
                  </button>
                )}
              </div>
            </div>
            
            {/* Profile Section */}
            <div className="flex items-center space-x-4">
              <div className="relative">
                <img 
                  src={currentContact.avatar} 
                  alt={currentContact.name}
                  className="w-16 h-16 rounded-full object-cover border-3 border-white/30"
                />
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white"></div>
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold mb-1">{currentContact.name}</h2>
                <p className="text-blue-100 text-sm">{currentContact.role}</p>
                <p className="text-blue-200 text-xs">{currentContact.company}</p>
              </div>
            </div>
          </div>

          {/* Card Body */}
          <div className="p-6 space-y-4">
            {/* Priority Badge */}
            <div className="flex items-center justify-between">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white ${priorityColors[currentContact.priority]}`}>
                <Star className="w-3 h-3 mr-1" />
                {priorityLabels[currentContact.priority]}
              </span>
              <span className="text-xs text-gray-500">#{currentContactIndex + 1} of {contacts.length}</span>
            </div>

            {/* Contact Details */}
            <div className="space-y-3">
              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <MapPin className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Met at</p>
                  <p className="text-sm text-gray-900 font-medium">{currentContact.location}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <MessageCircle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Interests</p>
                  <p className="text-sm text-gray-900 font-medium">{currentContact.interest}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <Calendar className="w-5 h-5 text-pink-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Follow up</p>
                  <p className="text-sm text-gray-900 font-medium">{currentContact.followup}</p>
                </div>
              </div>
            </div>

            {/* Contact Actions */}
            <div className="flex space-x-2 pt-4 border-t border-gray-100">
              <button className="flex-1 flex items-center justify-center space-x-2 py-3 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors">
                <Phone className="w-4 h-4" />
                <span className="text-sm font-medium">Call</span>
              </button>
              <button className="flex-1 flex items-center justify-center space-x-2 py-3 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors">
                <Mail className="w-4 h-4" />
                <span className="text-sm font-medium">Email</span>
              </button>
              <button className="flex-1 flex items-center justify-center space-x-2 py-3 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors">
                <MessageCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Message</span>
              </button>
            </div>
          </div>

          {/* Status Indicator */}
          <div className="px-6 pb-6">
            <div className="text-center">
              <div className={`inline-flex items-center px-4 py-2 rounded-full text-xs transition-all duration-500 ${
                hasReachedEnd
                  ? 'bg-green-100 text-green-700'
                  : animationPhase !== 'visible' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                <div className={`w-2 h-2 rounded-full mr-2 transition-all duration-500 ${
                  hasReachedEnd
                    ? 'bg-green-500'
                    : animationPhase !== 'visible' 
                    ? 'bg-blue-500 animate-pulse' 
                    : 'bg-gray-400'
                }`} />
                {hasReachedEnd 
                  ? 'Final contact reached' 
                  : animationPhase !== 'visible' 
                  ? 'Loading next contact...' 
                  : 'ContaX Smart Shuffle'
                }
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Dots */}
        <div className="flex justify-center space-x-2 mt-4">
          {contacts.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-all duration-500 ${
                index === currentContactIndex 
                  ? 'bg-blue-600 w-6' 
                  : index < currentContactIndex
                  ? 'bg-blue-300'
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnimatedContactCard;